/**
 * The transcoding pipeline, as the panel sees it.
 *
 * Rows come from `media_jobs`, which klooma_db_api owns and admin-api reads
 * out of the database they share. Design:
 * docs/superpowers/specs/2026-09-08-catalogue-media-panel-design.md
 */

/** Anything but `queued` and `running` is final. */
export type MediaJobStatusValue =
  | "queued"
  | "running"
  | "succeeded"
  | "failed"
  | "canceled";

export const TERMINAL_STATUSES: MediaJobStatusValue[] = [
  "succeeded",
  "failed",
  "canceled",
];

/** Which entity a job belongs to. Also the first segment of its object path. */
export type MediaDomain =
  | "feed"
  | "fan-letter"
  | "movie"
  | "tv"
  /** A show's own trailer/teaser/clip - one id, the show's. An episode's video stays under "tv". */
  | "tv-show"
  | "music-video"
  | "podcast"
  /** A podcast show's own trailer/clip - one id, the show's. An episode's audio/video stays under "podcast". */
  | "podcast-show"
  | "live"
  /** A song's own master upload (`MediaUploadController::ABILITY_BY_DOMAIN`, `music:write`). */
  | "song";

/** Which of an entity's assets a job produced. */
export type MediaRole = "main" | "trailer" | "preview" | "teaser" | "clip" | "audio";

export interface MediaJobProbe {
  duration?: number;
  width?: number;
  height?: number;
  codec?: string;
  fps?: number;
  bitrate_kbps?: number;
}

export interface MediaJobOutputs {
  hls?: string | null;
  mp4?: string | null;
  poster?: string | null;
  variants?: string[];
  asset_class?: string;
}

/**
 * Where a job's wall clock went, in milliseconds, as the transcoder measured it.
 *
 * `duration_seconds` on its own says a job was slow; this says at what. Forty
 * minutes of `download_ms` is a network problem and forty minutes of
 * `encode_ms` is a ladder problem, and the two have nothing in common.
 */
export interface MediaJobTimings {
  download_ms?: number;
  probe_ms?: number;
  encode_ms?: number;
  poster_ms?: number;
  upload_ms?: number;
  total_ms?: number;
}

export interface MediaJob {
  id: number;
  job_ulid: string;
  domain: MediaDomain;
  entity_id: number;
  role: MediaRole;
  profile: string;
  lane: "interactive" | "batch";
  source_uri: string;
  output_prefix: string;
  asset_class: string | null;
  status: MediaJobStatusValue;
  progress: number;
  /** `probing`, `encoding 720p`, `uploading` — what it is doing right now. */
  stage: string | null;
  attempts: number;
  error_code: string | null;
  error_message: string | null;
  probe: MediaJobProbe | null;
  outputs: MediaJobOutputs | null;
  timings: MediaJobTimings | null;
  /** Computed by admin-api. Counts up while a job is still running. */
  duration_seconds: number | null;
  /**
   * Seconds of finished video per second of encoding.
   *
   * The comparable number: wall clock says a feature took four hours and a
   * trailer four minutes, which only says one is longer. 0.5x against 3x says
   * which one the ladder struggled with.
   */
  encode_speed: number | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  updated_at: string;
  /** Resolved by admin-api: the title, not the id. */
  label?: string | null;
  link?: string | null;
  /** Running, but silent for longer than the sweeper's patience. */
  is_stalled?: boolean;
  /**
   * Computed by admin-api, not stored: for a `queued` job with
   * `stage: "waiting_for_capacity"`, how many capped batch jobs are ordered
   * ahead of it - 0 for whichever one is released next. `null` for every
   * other job.
   */
  queue_position?: number | null;
}

export interface MediaJobSummary {
  running: number;
  queued: number;
  failed_today: number;
  succeeded_today: number;
  /** The alarm. Anything but zero is a problem nobody has noticed yet. */
  stalled: number;
}

export interface UploadSession {
  upload_id: string;
  upload_url: string;
  /** Must be a multiple of 262144; GCS rejects anything else mid-upload. */
  chunk_bytes: number;
  expires_at: string;
}

/** Jobs for a page of content, keyed by entity id then role. */
export type MediaJobsByEntity = Record<number, Partial<Record<MediaRole, MediaJob>>>;

export function isTerminal(status: MediaJobStatusValue): boolean {
  return TERMINAL_STATUSES.includes(status);
}
